import React, { useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { Form, Modal, ModalBody, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';
import { t, ts } from '../../Components/Common/theme';

/* ─── Field wrapper ────────────────────────────────── */
const Field = ({ label, required, error, children }) => (
    <div>
        <label style={ts.label}>
            {label}{required && <span style={{ color: t.danger, marginLeft: 2 }}>*</span>}
        </label>
        {children}
        {error && <div style={{ fontSize: 11, color: t.danger, marginTop: 3 }}>{error}</div>}
    </div>
);

/* ─── Section divider ──────────────────────────────── */
const Section = ({ title }) => <div style={ts.sectionLabel}>{title}</div>;

/* ─── Avatar / file picker ─────────────────────────── */
const AvatarPicker = ({ previewImage, onChange }) => {
    const inputRef = React.useRef(null);
    const [dragging, setDragging] = React.useState(false);

    const handleDrop = e => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) onChange({ target: { files: [file] } });
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{
                width: 64, height: 64, borderRadius: 12, flexShrink: 0, overflow: 'hidden',
                background: t.primaryLight, border: `0.5px solid ${t.primaryMid}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {previewImage ? (
                    <img src={previewImage} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={t.primary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                    </svg>
                )}
            </div>

            <div
                onClick={() => inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                style={{
                    flex: 1, height: 64,
                    border: `1px dashed ${dragging ? t.primary : t.borderInput}`,
                    borderRadius: 10,
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '0 16px', cursor: 'pointer',
                    background: dragging ? t.primaryLight : t.surface,
                    transition: 'border-color .15s, background .15s',
                    userSelect: 'none',
                }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke={dragging ? t.primary : t.textHint}
                    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 16 12 12 8 16" />
                    <line x1="12" y1="12" x2="12" y2="21" />
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                </svg>
                <span style={{ fontSize: 13, fontWeight: 500, color: t.text }}>
                    {previewImage ? 'Click or drag to replace photo' : 'Click or drag photo here'}
                </span>
                <span style={{ fontSize: 12, color: t.textHint }}>— PNG, JPG up to 5 MB</span>
                {previewImage && (
                    <span style={{ ...ts.badge, marginLeft: 'auto' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={t.primaryText} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Photo selected
                    </span>
                )}
                <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onChange} />
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
const Register = () => {
    const history = useNavigate();

    const [departments, setDepartments] = useState([]);
    const [supervisors, setSupervisors] = useState([]);
    const [nextEmpId, setNextEmpId] = useState('');
    const [roles, setRoles] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewImage, setPreviewImage] = useState('');
    const [errorModal, setErrorModal] = useState(false);
    const [successModal, setSuccessModal] = useState(false);

    useEffect(() => {
        const load = async () => {
            // Round HR-PA follow-up (2026-08-17): the department list comes
            // from the /api/department EPICOR proxy, which can be down while
            // everything else works. It must never block employee creation —
            // the three registration lookups are essential (supervisor list,
            // the assigned Next ID, the role list), the department list is a
            // convenience: when it fails the Department field degrades to a
            // plain code input (the server still requires a non-blank code).
            // Owner clarification (2026-08-17): the DROPDOWN is needed for
            // adding an employee — only department ADMINISTRATION was dropped.
            // The list now comes from GET registration/departments, the
            // existing endpoint that reads the Epicor SQL replica
            // (erp.JcDept, Company='SSW') — the same proven connection the
            // roles / supervisors / next-ID lookups on this form already
            // use — instead of the Epicor REST proxy (/api/department),
            // which is currently down and is no longer called by any screen.
            const [deptRes, supRes, empIdRes, rolesRes] = await Promise.allSettled([
                api.get('registration/departments'),
                api.get('registration/supervisors'),
                api.get('registration/next-empid'),
                api.get('registration/roles'),
            ]);
            if (deptRes.status === 'fulfilled' && Array.isArray(deptRes.value.data)) {
                const list = deptRes.value.data
                    .map(d => ({ jcDept1: String(d.id ?? ''), description: d.description || '' }))
                    .filter(d => d.jcDept1)
                    .sort((x, y) => (x.description || x.jcDept1).localeCompare(y.description || y.jcDept1));
                setDepartments(list);
            } else {
                setDepartments([]);
                toast.warn('Department list unavailable — type the department code manually.');
            }
            if (supRes.status === 'fulfilled' && supRes.value.data) setSupervisors(supRes.value.data);
            if (empIdRes.status === 'fulfilled' && empIdRes.value.data) setNextEmpId(empIdRes.value.data);
            if (rolesRes.status === 'fulfilled' && rolesRes.value.data) setRoles(rolesRes.value.data);
            if (supRes.status !== 'fulfilled' || empIdRes.status !== 'fulfilled' || rolesRes.status !== 'fulfilled') {
                toast.error('Failed to load form data. Please refresh.');
                setErrorModal(true);
            }
        };
        load();
    }, []);

    const handleFileChange = e => {
        const file = e.target.files[0];
        if (!file) return;
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onloadend = () => setPreviewImage(reader.result);
        reader.readAsDataURL(file);
    };

    // HR Round 6 / Issue 10 — advisory address suggestion. On ZIP blur, ask
    // /api/address/suggest (provider-driven; default = City/State combos the
    // directory already holds for that ZIP; "none" disables). Blank city/state
    // are auto-filled from the top suggestion; a mismatch only informs — it
    // never blocks the save (HR keeps the final word on unusual addresses).
    // HR Round 8 — DMV-portal-style street autocomplete. Debounced type-ahead
    // against /api/address/autocomplete (provider + credentials live in the
    // API's appsettings; the browser never sees a key). Picking a suggestion
    // fills street/city/state/ZIP. With the provider set to "none" the
    // endpoint answers { enabled:false } and this UI never appears.
    const [acItems, setAcItems] = useState([]);
    const [acOpen, setAcOpen] = useState(false);
    const [acBusy, setAcBusy] = useState(false);
    const acTimer = React.useRef(null);
    const acSkipNext = React.useRef(false);

    const acSearch = (text) => {
        if (acTimer.current) clearTimeout(acTimer.current);
        if (acSkipNext.current) { acSkipNext.current = false; return; }
        const q = String(text || '').trim();
        if (q.length < 4) { setAcItems([]); setAcOpen(false); return; }
        acTimer.current = setTimeout(async () => {
            try {
                setAcBusy(true);
                const r = await api.get('address/autocomplete', { params: { q } });
                if (r.data?.enabled && Array.isArray(r.data.items) && r.data.items.length) {
                    setAcItems(r.data.items);
                    setAcOpen(true);
                } else { setAcItems([]); setAcOpen(false); }
            } catch { setAcItems([]); setAcOpen(false); }
            finally { setAcBusy(false); }
        }, 300);
    };

    const acPick = async (item) => {
        setAcOpen(false);
        let it = item;
        if (item.placeId && !item.street) {
            try { it = (await api.get('address/expand', { params: { placeId: item.placeId } })).data || item; }
            catch { it = item; }
        }
        acSkipNext.current = true;
        if (it.street) f.setFieldValue('address1', it.street);
        else if (it.label) f.setFieldValue('address1', it.label.split(',')[0]);
        if (it.street2) f.setFieldValue('address2', it.street2);
        if (it.city) f.setFieldValue('city', it.city);
        if (it.state) f.setFieldValue('state', it.state);
        if (it.zip) f.setFieldValue('postalcode', it.zip);
    };

    const suggestFromZip = async (zip, values, setFieldValue) => {
        const digits = String(zip || '').replace(/\D/g, '');
        if (digits.length < 5) return;
        try {
            const r = await api.get('address/suggest', { params: { zip: digits.slice(0, 5) } });
            const top = r.data?.suggestions?.[0];
            if (!top) return;
            if (!values.city)  setFieldValue('city', top.city);
            if (!values.state) setFieldValue('state', top.state);
            if (values.city && values.state &&
                (values.city.toLowerCase() !== top.city.toLowerCase()
                 || values.state.toLowerCase() !== top.state.toLowerCase())) {
                toast.info(`ZIP ${digits.slice(0, 5)} is usually ${top.city}, ${top.state} — double-check the city/state.`,
                    { autoClose: 6000 });
            }
        } catch { /* advisory only — never interrupt the form */ }
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            roleId: '', first_name: '', last_name: '', middle_name: '',
            title: '', gender: '', dob: '', email: '',
            address1: '', address2: '', city: '', state: '', postalcode: '',
            phone: '', emgcontact: '', emgphone: '', expensecode: '', department: '',
            supervisor: '', hireDate: '', type: '',
            // HR-UAT-2: epolimit removed with its field — the submit still
            // sends the approved default (fd.append('epoLimit', 0)).
            ftooffset: '0', temporary: false,
        },
        validationSchema: Yup.object({
            first_name: Yup.string().required('First name is required'),
            last_name: Yup.string().required('Last name is required'),
            title: Yup.string().required('Title is required'),
            roleId: Yup.string().notOneOf([''], 'Role is required').required('Role is required'),
            dob: Yup.string(),   // Round 2h (owner): optional
            gender: Yup.string().notOneOf([''], 'Gender is required').required('Gender is required'),
            address1: Yup.string(),   // Round 2h: address block optional
            address2: Yup.string(),
            city: Yup.string(),
            state: Yup.string(),
            postalcode: Yup.string(),
            phone: Yup.string().required('Phone is required'),
            expensecode: Yup.string().notOneOf([''], 'Rate type is required').required('Rate type is required'),
            department: Yup.string().notOneOf([''], 'Department is required').required('Department is required'),
            supervisor: Yup.string().notOneOf([''], 'Supervisor is required').required('Supervisor is required'),
            hireDate: Yup.string().required('Hire date is required'),
        }),
        onSubmit: async (values, { resetForm }) => {
            const fd = new FormData();
            fd.append('empID', nextEmpId);
            fd.append('firstName', values.first_name);
            fd.append('mi', values.middle_name || '');
            fd.append('lastName', values.last_name);
            fd.append('street1', values.address1);
            fd.append('street2', values.address2);
            fd.append('city', values.city);
            fd.append('state', values.state);
            fd.append('zip', values.postalcode);
            fd.append('country', 'USA');
            fd.append('phone', values.phone);
            fd.append('emgContact', values.emgcontact);
            fd.append('emgPhone', values.emgphone || '');   // Round 2h
            // Batch 7 fix: dob was collected by the form (Yup required) but
            // never appended to FormData. That's why /add-employee was 400-
            // ing with no useful message. Server-side EmployeeDto has a
            // non-nullable DateTime `dob`, so when this field is omitted
            // the [ApiController] auto-validates a model-state error.
            fd.append('dob', values.dob);
            fd.append('expenseCode', values.expensecode === 'Salary' ? 'IDL' : 'DL');
            fd.append('dept', values.department);
            fd.append('supervisor', values.supervisor);
            // Owner policy (2026-08-17): ONE company shift, 8:00 AM–4:30 PM.
            // The Shift field was removed from this form; every new employee
            // is recorded on shift 1 (the DTO's short is non-nullable). All
            // late/early calculations already resolve shift 1 / null to the
            // 08:00 standard start.
            fd.append('shift', 1);
            fd.append('hireDate', values.hireDate);
            fd.append('type', values.type);
            fd.append('roleId', parseInt(values.roleId, 10));
            fd.append('epoLimit', 0);
            fd.append('grade', 0);
            fd.append('rate', 0);
            fd.append('empStatus', 'A');
            fd.append('extension', '');
            fd.append('email', values.email);
            fd.append('title', values.title);
            fd.append('gender', values.gender);
            fd.append('ftoOffset', 0);
            fd.append('temporary', values.temporary ? 'true' : 'false');   // Round 3 (G-42)
            if (selectedImage) fd.append('profileImage', selectedImage);

            try {
                const r = await api.post('registration/add-employee', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                // Treat HTTP 2xx as success. (Previously this matched the word
                // "successfully" in the message, which broke when the backend
                // message changed to "Employee created in Epicor and locally."
                // — a successful create then showed a false "failed" error.)
                if (r.status >= 200 && r.status < 300) {
                    setSuccessModal(true);
                    resetForm();
                    setPreviewImage('');
                    setSelectedImage(null);
                    // Advance to the next available ID so a subsequent add does
                    // not reuse the ID we just created (which the server-side
                    // duplicate guard would reject with a 409 "already exists").
                    try {
                        const expensecode = values.expensecode;
                        const type = expensecode === 'Salary' ? 'salaried' : 'hourly';
                        const idRes = await api.get(`registration/next-empid?type=${type}`);
                        if (idRes.data) setNextEmpId(idRes.data);
                    } catch { /* keep going; next id will refresh on type change */ }
                } else {
                    toast.error('Registration failed. Please try again.');
                }
            } catch (e) {
                // Batch 7 fix: surface field-level errors from the server
                // instead of swallowing them into a generic "Server error".
                // Backend now returns { message, fieldErrors: [{field, errors}] }
                // for any 400. Show the first one so the user knows what to
                // fix.
                const body = e?.response?.data;
                if (body?.fieldErrors?.length) {
                    const first = body.fieldErrors[0];
                    const errText = (first.errors || []).join('; ');
                    toast.error(`${first.field}: ${errText}`);
                } else if (body?.message) {
                    toast.error(body.message);
                } else {
                    toast.error('Server error. Please try again later.');
                }
            }
        },
    });

    const f = validation;

    // Batch 7 (G-19): re-fetch next-empid when the rate type changes so the
    // employee gets an ID from the matching range (salaried 3060–3999 vs
    // hourly 7000–7999). Without this, switching the dropdown after first
    // load would keep the wrong-range ID and the server-side range check
    // would reject the submission with "EmpID is outside the <type> range".
    useEffect(() => {
        const expensecode = validation?.values?.expensecode;
        if (!expensecode) return;
        const type = expensecode === 'Salary' ? 'salaried' : 'hourly';
        api.get(`registration/next-empid?type=${type}`)
           .then(r => { if (r.data) setNextEmpId(r.data); })
           .catch(() => {/* keep previous nextEmpId on failure */});
    }, [validation.values.expensecode]);
    const err = field => f.touched[field] && f.errors[field] ? f.errors[field] : '';
    const inp = (name, extra = {}) => ({
        name,
        value: f.values[name] ?? '',
        onChange: f.handleChange,
        onBlur: f.handleBlur,
        style: { ...ts.input, ...(err(name) ? { borderColor: t.danger } : {}) },
        ...extra,
    });

    return (
        <React.Fragment>
            <ToastContainer />
            <Form onSubmit={e => { e.preventDefault(); f.handleSubmit(); }}>

                {/* ── Header ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: `0.5px solid ${t.border}` }}>
                    <div style={ts.iconWrap}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={t.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <line x1="19" y1="8" x2="19" y2="14" />
                            <line x1="22" y1="11" x2="16" y2="11" />
                        </svg>
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 500, color: t.text }}>Add new employee</div>
                        <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>Fill in all required fields to register a new employee</div>
                    </div>
                    <span style={{ ...ts.badge, marginLeft: 'auto' }}>Next ID: {nextEmpId || '...'}</span>
                </div>

                {/* ── Avatar picker ── */}
                <AvatarPicker previewImage={previewImage} onChange={handleFileChange} />

                {/* ── Personal information ── */}
                <Section title="Personal information" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12 }}>
                    <Field label="Role" required error={err('roleId')}>
                        <select {...inp('roleId')} style={{ ...ts.input, ...(err('roleId') ? { borderColor: t.danger } : {}) }}>
                            <option value="">Select role</option>
                            {roles.map(r => <option key={r.roleId} value={r.roleId}>{r.roleName}</option>)}
                        </select>
                    </Field>
                    <Field label="First name" required error={err('first_name')}>
                        <input {...inp('first_name')} placeholder="First name" />
                    </Field>
                    <Field label="Middle initial">
                        <input {...inp('middle_name')} placeholder="M.I." />
                    </Field>
                    <div style={{ gridColumn: 'span 2' }}>
                        <Field label="Last name" required error={err('last_name')}>
                            <input {...inp('last_name')} placeholder="Last name" />
                        </Field>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginTop: 12 }}>
                    <Field label="Gender" required error={err('gender')}>
                        <select {...inp('gender')} style={{ ...ts.input, ...(err('gender') ? { borderColor: t.danger } : {}) }}>
                            <option value="">Select gender</option>
                            <option>Male</option><option>Female</option><option>Other</option>
                        </select>
                    </Field>
                    <Field label="Date of birth" error={err('dob')}>
                        <Flatpickr
                            style={{ ...ts.input, ...(err('dob') ? { borderColor: t.danger } : {}) }}
                            value={f.values.dob}
                            onChange={(_, dateStr) => f.setFieldValue('dob', dateStr)}
                            options={{ dateFormat: 'Y-m-d' }}
                            placeholder="YYYY-MM-DD"
                        />
                    </Field>
                    <Field label="Title / designation" required error={err('title')}>
                        <input {...inp('title')} placeholder="e.g. Software Engineer" />
                    </Field>
                    <Field label="Email address" error={err('email')}>
                        <input {...inp('email')} type="email" placeholder="name@sswhite.net" />
                    </Field>
                </div>

                {/* ── Address ── */}
                <Section title="Address" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12 }}>
                    <Field label="Address line 1" error={err('address1')}>
                        <div style={{ position: 'relative' }}>
                            <input {...inp('address1')} placeholder="Start typing the street address…"
                                autoComplete="off"
                                onChange={(e) => { f.handleChange(e); acSearch(e.target.value); }}
                                onBlur={(e) => { f.handleBlur(e); setTimeout(() => setAcOpen(false), 200); }} />
                            {acOpen && (
                                <div style={{
                                    position: 'absolute', zIndex: 1050, left: 0, right: 0, top: '100%',
                                    background: '#fff', border: `0.5px solid ${t.border}`, borderRadius: 8,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4, overflow: 'hidden',
                                }}>
                                    {acItems.map((it, i) => (
                                        <div key={i}
                                             onMouseDown={(e) => { e.preventDefault(); acPick(it); }}
                                             style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: i < acItems.length - 1 ? `0.5px solid ${t.border}` : 'none' }}
                                             onMouseEnter={(e) => { e.currentTarget.style.background = t.primaryLight; }}
                                             onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                                            <i className="ri-map-pin-line align-bottom me-1" style={{ color: t.primary }} />
                                            {it.label}
                                        </div>
                                    ))}
                                    <div style={{ padding: '4px 12px', fontSize: 11, color: '#98a2b3', background: '#fafafa' }}>
                                        {acBusy ? 'Searching…' : 'Pick a suggestion to fill city, state, and ZIP'}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Field>
                    <Field label="Address line 2" error={err('address2')}>
                        <input {...inp('address2')} placeholder="Apt, suite, etc." />
                    </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12, marginTop: 12 }}>
                    <Field label="City" error={err('city')}><input {...inp('city')} placeholder="City" /></Field>
                    <Field label="State" error={err('state')}><input {...inp('state')} placeholder="State" /></Field>
                    <Field label="Postal code" error={err('postalcode')}>
                        <input {...inp('postalcode')} placeholder="ZIP"
                            onBlur={(e) => { f.handleBlur(e); suggestFromZip(e.target.value, f.values, f.setFieldValue); }} />
                    </Field>
                    <Field label="Phone" required error={err('phone')}><input {...inp('phone')} placeholder="Phone" /></Field>
                    <Field label="Emergency contact name" error={err('emgcontact')}><input {...inp('emgcontact')} placeholder="Full name" /></Field>
                    {/* Round 2h: emergency PHONE — empbasic.emgphone existed since Vantage
                        but was never exposed by the UI; no DB change needed. */}
                    <Field label="Emergency phone" error={err('emgphone')}><input {...inp('emgphone')} placeholder="Phone" /></Field>
                </div>

                {/* ── Employment details ── */}
                <Section title="Employment details" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12 }}>
                    <Field label="Rate type" required error={err('expensecode')}>
                        <select {...inp('expensecode')} style={{ ...ts.input, ...(err('expensecode') ? { borderColor: t.danger } : {}) }}>
                            <option value="">Select rate type</option>
                            <option>Salary</option><option>Hourly</option>
                        </select>
                    </Field>
                    <div style={{ gridColumn: 'span 3' }}>
                        <Field label="Department" required error={err('department')}>
                            {departments.length > 0 ? (
                                <select {...inp('department')} style={{ ...ts.input, ...(err('department') ? { borderColor: t.danger } : {}) }}>
                                    <option value="">Select department</option>
                                    {departments.map(d => <option key={d.jcDept1} value={d.jcDept1}>{d.description ? `${d.jcDept1} — ${d.description}` : d.jcDept1}</option>)}
                                </select>
                            ) : (
                                <>
                                    <input {...inp('department')} placeholder="Department code (e.g. 100)"
                                        style={{ ...ts.input, ...(err('department') ? { borderColor: t.danger } : {}) }} />
                                    <small style={{ color: t.textSub }}>
                                        Department list unavailable — enter the Epicor JC department code directly.
                                    </small>
                                </>
                            )}
                        </Field>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginTop: 12 }}>
                    <div style={{ gridColumn: 'span 2' }}>
                        <Field label="Supervisor" required error={err('supervisor')}>
                            <select {...inp('supervisor')} style={{ ...ts.input, ...(err('supervisor') ? { borderColor: t.danger } : {}) }}>
                                <option value="">Select supervisor</option>
                                {supervisors.map(s => <option key={s.empID} value={s.empID}>{s.empID} — {s.name}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Employment type" error={err('type')}>
                        <select {...inp('type')} style={{ ...ts.input, ...(err('type') ? { borderColor: t.danger } : {}) }}>
                            <option value="">Select type</option>
                            <option>Temp Worker</option><option>Factory Worker</option>
                            <option>Staff</option><option>Management</option>
                        </select>
                    </Field>
                    <Field label="Hire date" required error={err('hireDate')}>
                        {/* HR-UAT-1 (2026-08-24): Hire Date is DATE ONLY — the
                            time picker (enableTime + 'Y-m-d H:i') is gone. The
                            value is the calendar string flatpickr formatted in
                            LOCAL time (same pattern as the DOB field above),
                            NOT date[0].toISOString(): toISOString converts the
                            selected local midnight to UTC, which shifted the
                            hire date back a day for US timezones. The backend
                            (EmployeeService.NormalizeDate) already stores the
                            plain date — no schema or storage change. */}
                        <Flatpickr
                            style={{ ...ts.input, ...(err('hireDate') ? { borderColor: t.danger } : {}) }}
                            value={f.values.hireDate}
                            onChange={(_, dateStr) => f.setFieldValue('hireDate', dateStr || '')}
                            options={{ dateFormat: 'Y-m-d' }}
                            placeholder="Select date"
                        />
                    </Field>
                </div>

                {/* HR-UAT-2 (2026-08-24): EPO limit removed from Add Employee —
                    UI only. The submit handler has always sent the approved
                    creation default (fd.append('epoLimit', 0)); the input's
                    value was never read, so nothing about creation changes.
                    Per-employee EPO limits stay administered exclusively via
                    Admin → Employee EPO Limit Assignment (empbasic.number01);
                    Employees.epolimit, the EPO APIs and approval-limit logic
                    are all untouched. Row rebalanced 4 → 3 columns. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginTop: 12 }}>
                    <Field label="FTO offset"><input {...inp('ftooffset')} placeholder="0" /></Field>
                    <Field label="Employee ID">
                        <input value={nextEmpId} readOnly style={{ ...ts.input, background: t.surface, color: t.textSub }} />
                    </Field>
                    {/* Round 3 (G-42): Temporary / Intern Employee flag — legacy CheckBoxTemp parity */}
                    <Field label="Temporary / Intern Employee">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', height: '100%' }}>
                            <input
                                type="checkbox"
                                checked={!!f.values.temporary}
                                onChange={(e) => f.setFieldValue('temporary', e.target.checked)}
                            />
                            <span style={{ color: t.textSub, fontSize: 13 }}>Mark as temporary / intern</span>
                        </label>
                    </Field>
                </div>

                {/* ── Footer ── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1.5rem', paddingTop: '1rem', borderTop: `0.5px solid ${t.border}` }}>
                    <button type="button" style={ts.btnGhost}
                        onClick={() => { f.resetForm(); setPreviewImage(''); setSelectedImage(null); }}>
                        Reset form
                    </button>
                    <button type="submit" style={ts.btnPrimary}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <line x1="19" y1="8" x2="19" y2="14" />
                            <line x1="22" y1="11" x2="16" y2="11" />
                        </svg>
                        Add employee
                    </button>
                </div>
            </Form>

            {/* Error modal */}
            <Modal isOpen={errorModal} centered backdrop="static">
                <div style={{ background: t.dangerLight, padding: '1.25rem 1.5rem', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={t.dangerText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span style={{ fontSize: 14, fontWeight: 500, color: t.dangerText }}>Server error</span>
                </div>
                <ModalBody className="text-center p-4">
                    <p style={{ color: t.textSub, marginBottom: 16 }}>Unable to connect to the server. Please try again later.</p>
                    <button style={ts.btnPrimary} onClick={() => history('/')}>Go back to dashboard</button>
                </ModalBody>
            </Modal>

            {/* Success modal */}
            <Modal isOpen={successModal} toggle={() => setSuccessModal(false)} centered>
                <ModalHeader toggle={() => setSuccessModal(false)}>
                    <span style={{ fontSize: 15, fontWeight: 500 }}>Employee registered</span>
                </ModalHeader>
                <ModalBody className="text-center p-5">
                    <lord-icon src="https://cdn.lordicon.com/lupuorrc.json" trigger="loop"
                        colors="primary:#2b3f8c,secondary:#b8c3e8"
                        style={{ width: 100, height: 100 }} />
                    <p style={{ color: t.textSub, marginTop: 16, marginBottom: 20 }}>
                        New employee has been successfully added.
                    </p>
                    <button style={ts.btnPrimary} onClick={() => setSuccessModal(false)}>Done</button>
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default Register;